import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchClassificationLatest,
  fetchClassificationToday,
  fetchClassificationHistory,
  fetchClassificationByDate,
  triggerClassification,
} from '../api';

const DAY_TYPE_COLORS = {
  RANGE_TIGHT: { bg: '#1e3a5f', fg: '#90caf9', label: 'Range (Tight)' },
  RANGE_MODERATE: { bg: '#1e4d3f', fg: '#a5d6a7', label: 'Range (Moderate)' },
  TREND_BULLISH: { bg: '#1b5e20', fg: '#c8e6c9', label: 'Trend Bullish' },
  TREND_BEARISH: { bg: '#5e1b1b', fg: '#ffcdd2', label: 'Trend Bearish' },
  SKIP: { bg: '#4a4a4a', fg: '#e0e0e0', label: 'Skip' },
};

function DayTypeBadge({ type, confidence }) {
  const cfg = DAY_TYPE_COLORS[type] || { bg: '#333', fg: '#fff', label: type || '—' };
  return (
    <span
      style={{
        background: cfg.bg,
        color: cfg.fg,
        padding: '4px 10px',
        borderRadius: 6,
        fontWeight: 600,
        fontSize: 13,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      {cfg.label}
      {confidence != null && (
        <span style={{ opacity: 0.85, fontWeight: 500 }}>
          · {(confidence * 100).toFixed(0)}%
        </span>
      )}
    </span>
  );
}

function MetricCell({ label, value, suffix = '', precision = 2 }) {
  const display = (value == null || Number.isNaN(value))
    ? '—'
    : (typeof value === 'number' ? value.toFixed(precision) : String(value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', minWidth: 90 }}>
      <span style={{ fontSize: 11, color: '#9aa0a6', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </span>
      <span style={{ fontSize: 14, fontWeight: 600, color: '#e8eaed' }}>
        {display}{suffix}
      </span>
    </div>
  );
}

function SnapshotCard({ snap, isLive = false }) {
  const f = snap.features || {};
  const scores = snap.scores || {};
  const ruleLog = Array.isArray(snap.rule_log) ? snap.rule_log : [];
  const [expanded, setExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);

  // Group rule hits by target day type, sorted by score desc
  const hitsByTarget = useMemo(() => {
    const grouped = {};
    ruleLog.forEach((h) => {
      const t = h.target || 'UNKNOWN';
      if (!grouped[t]) grouped[t] = [];
      grouped[t].push(h);
    });
    Object.values(grouped).forEach((arr) =>
      arr.sort((a, b) => (b.score || 0) - (a.score || 0))
    );
    return grouped;
  }, [ruleLog]);

  // Order so the winning type is first
  const targetOrder = useMemo(() => {
    const all = Object.keys(hitsByTarget);
    all.sort((a, b) => {
      if (a === snap.day_type) return -1;
      if (b === snap.day_type) return 1;
      return (scores[b] || 0) - (scores[a] || 0);
    });
    return all;
  }, [hitsByTarget, snap.day_type, scores]);

  return (
    <div
      style={{
        background: '#1f2024',
        border: '1px solid #2e3036',
        borderRadius: 8,
        padding: 14,
        marginBottom: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 13, color: '#9aa0a6', minWidth: 70 }}>
          <strong style={{ color: '#e8eaed' }}>
            {isLive ? 'LIVE' : snap.check_label}
          </strong>
          <div style={{ fontSize: 11 }}>{snap.check_type}</div>
        </div>
        <DayTypeBadge type={snap.day_type} confidence={snap.confidence} />
        <div style={{ flex: 1, fontSize: 13, color: '#bdc1c6' }}>{snap.reason}</div>
        <button
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: 'transparent',
            color: '#8ab4f8',
            border: '1px solid #3c4043',
            borderRadius: 4,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 12,
          }}
        >
          {expanded ? 'Hide' : 'Details'}
        </button>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, marginTop: 12 }}>
        <MetricCell label="Nifty" value={snap.nifty_price} precision={2} />
        <MetricCell label="VIX" value={snap.vix_value} precision={2} />
        <MetricCell label="VIX Δ%" value={snap.vix_change_pct} suffix="%" />
        <MetricCell label="Gap %" value={snap.gap_pct} suffix="%" />
        <MetricCell label="OR %" value={snap.or_pct} suffix="%" />
        <MetricCell label="ADX" value={snap.adx} precision={1} />
        <MetricCell label="RSI" value={snap.rsi} precision={1} />
        <MetricCell label="Breadth" value={snap.breadth_score} precision={2} />
        <MetricCell label="VWAP Δ%" value={snap.vwap_distance_pct} suffix="%" />
      </div>

      {expanded && (
        <div style={{ marginTop: 14, borderTop: '1px solid #2e3036', paddingTop: 12 }}>
          <div style={{ marginBottom: 12 }}>
            <strong style={{ color: '#e8eaed', fontSize: 13 }}>Total Scores</strong>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 6 }}>
              {Object.entries(scores).map(([k, v]) => {
                const isWinner = k === snap.day_type;
                return (
                  <div
                    key={k}
                    style={{
                      background: isWinner ? '#2c5f2c' : '#2a2c31',
                      border: isWinner ? '1px solid #4caf50' : '1px solid transparent',
                      padding: '4px 10px',
                      borderRadius: 4,
                      fontSize: 12,
                      color: '#bdc1c6',
                    }}
                  >
                    <span style={{ color: '#9aa0a6' }}>{k}:</span>{' '}
                    <strong style={{ color: '#e8eaed' }}>{Number(v).toFixed(2)}</strong>
                  </div>
                );
              })}
            </div>
          </div>

          {snap.flags && snap.flags.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: '#e8eaed', fontSize: 13 }}>Flags</strong>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                {snap.flags.map((flag, i) => (
                  <span
                    key={i}
                    style={{
                      background: '#5e4a1b',
                      color: '#ffe0b2',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                    }}
                  >
                    {flag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* RULE TRACE — the WHY */}
          <div style={{ marginBottom: 12 }}>
            <strong style={{ color: '#e8eaed', fontSize: 13 }}>
              Why? Rule-by-rule evidence
            </strong>
            {ruleLog.length === 0 ? (
              <div style={{ color: '#9aa0a6', fontStyle: 'italic', marginTop: 6, fontSize: 12 }}>
                No rules fired (likely no data yet).
              </div>
            ) : (
              targetOrder.map((target) => {
                const cfg = DAY_TYPE_COLORS[target] || { bg: '#333', fg: '#fff', label: target };
                const isWinner = target === snap.day_type;
                const hits = hitsByTarget[target];
                const subtotal = hits.reduce((acc, h) => acc + (h.score || 0), 0);
                return (
                  <div
                    key={target}
                    style={{
                      marginTop: 10,
                      background: isWinner ? '#1c2a1c' : '#181a1f',
                      border: `1px solid ${isWinner ? '#4caf50' : '#2e3036'}`,
                      borderRadius: 6,
                      padding: 10,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <DayTypeBadge type={target} />
                      <span style={{ color: '#9aa0a6', fontSize: 12 }}>
                        {hits.length} rule{hits.length !== 1 ? 's' : ''} · subtotal {subtotal.toFixed(2)}
                      </span>
                      {isWinner && (
                        <span style={{
                          background: '#4caf50', color: '#0f1a0f',
                          padding: '2px 8px', borderRadius: 4,
                          fontSize: 11, fontWeight: 700,
                        }}>WINNER</span>
                      )}
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ color: '#9aa0a6', fontSize: 11, textTransform: 'uppercase' }}>
                          <th style={{ textAlign: 'left', padding: '4px 8px' }}>Category</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px' }}>Condition met</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px' }}>Observed</th>
                          <th style={{ textAlign: 'left', padding: '4px 8px' }}>Rule</th>
                          <th style={{ textAlign: 'right', padding: '4px 8px' }}>+Score</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hits.map((h, i) => (
                          <tr key={i} style={{ borderTop: '1px solid #2e3036' }}>
                            <td style={{ padding: '4px 8px', color: '#bdc1c6' }}>{h.category || '—'}</td>
                            <td style={{ padding: '4px 8px', color: '#e8eaed' }}>{h.condition}</td>
                            <td style={{ padding: '4px 8px', color: cfg.fg }}>
                              <strong>{h.value}</strong>
                            </td>
                            <td style={{ padding: '4px 8px', color: '#9aa0a6', fontFamily: 'monospace' }}>
                              {h.rule}
                            </td>
                            <td style={{ padding: '4px 8px', textAlign: 'right', color: '#a5d6a7', fontWeight: 600 }}>
                              +{Number(h.score).toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })
            )}
          </div>

          <div>
            <button
              onClick={() => setShowRaw((v) => !v)}
              style={{
                background: 'transparent', color: '#8ab4f8',
                border: '1px solid #3c4043', borderRadius: 4,
                padding: '3px 8px', cursor: 'pointer', fontSize: 11,
              }}
            >
              {showRaw ? 'Hide raw features' : 'Show raw features'}
            </button>
            {showRaw && (
              <pre
                style={{
                  background: '#15161a',
                  color: '#bdc1c6',
                  padding: 10,
                  borderRadius: 4,
                  fontSize: 11,
                  maxHeight: 240,
                  overflow: 'auto',
                  marginTop: 6,
                }}
              >
                {JSON.stringify(f, null, 2)}
              </pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DayClassifier() {
  const [today, setToday] = useState({ live: null, snapshots: [], date: '' });
  const [history, setHistory] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [datedSnapshots, setDatedSnapshots] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);

  const loadToday = useCallback(async () => {
    try {
      const [todayRes, latestRes] = await Promise.all([
        fetchClassificationToday(),
        fetchClassificationLatest(),
      ]);
      setToday({
        live: latestRes.data?.data || todayRes.data?.live || null,
        snapshots: todayRes.data?.data || [],
        date: todayRes.data?.trade_date || '',
      });
      setError(null);
    } catch (e) {
      console.error('Failed to load today classification', e);
      setError('Failed to load today classification');
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const res = await fetchClassificationHistory(30);
      setHistory(res.data?.data || []);
    } catch (e) {
      console.error('Failed to load classification history', e);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadToday(), loadHistory()]).finally(() => setLoading(false));
    const id = setInterval(loadToday, 15000);
    return () => clearInterval(id);
  }, [loadToday, loadHistory]);

  const loadDate = useCallback(async (date) => {
    if (!date) {
      setDatedSnapshots(null);
      return;
    }
    try {
      const res = await fetchClassificationByDate(date);
      setDatedSnapshots(res.data?.data || []);
    } catch (e) {
      console.error('Failed to load classification by date', e);
      setDatedSnapshots([]);
    }
  }, []);

  const handleDateChange = (e) => {
    const d = e.target.value;
    setSelectedDate(d);
    loadDate(d);
  };

  const handleTrigger = async () => {
    setTriggering(true);
    try {
      await triggerClassification();
      await loadToday();
    } catch (e) {
      console.error('Trigger failed', e);
      setError('Manual trigger failed');
    } finally {
      setTriggering(false);
    }
  };

  const liveSnap = today.live;

  const stats = useMemo(() => {
    if (!history.length) return null;
    const types = {};
    let changed = 0;
    let events = 0;
    history.forEach((d) => {
      types[d.dominant_day_type] = (types[d.dominant_day_type] || 0) + 1;
      if (d.changed_during_day) changed += 1;
      if (d.is_event_day) events += 1;
    });
    return { types, total: history.length, changed, events };
  }, [history]);

  return (
    <div style={{ padding: 20, color: '#e8eaed', background: '#15161a', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>🧭 Day-Type Classifier</h1>
          <div style={{ color: '#9aa0a6', fontSize: 13, marginTop: 4 }}>
            Observation-only. Refreshes every 15s. Persists at 09:45, 10:30, 11:30, 13:30, 14:45 IST plus event triggers.
          </div>
        </div>
        <button
          onClick={handleTrigger}
          disabled={triggering}
          style={{
            background: '#8ab4f8',
            color: '#15161a',
            border: 'none',
            borderRadius: 4,
            padding: '8px 14px',
            cursor: triggering ? 'wait' : 'pointer',
            fontWeight: 600,
          }}
        >
          {triggering ? 'Triggering…' : 'Force Classify Now'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#5e1b1b', color: '#ffcdd2', padding: 10, borderRadius: 4, marginBottom: 14 }}>
          {error}
        </div>
      )}

      {/* LIVE */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, color: '#bdc1c6', marginBottom: 10 }}>Live ({today.date || '—'})</h2>
        {liveSnap ? (
          <SnapshotCard snap={liveSnap} isLive />
        ) : (
          <div style={{ color: '#9aa0a6', fontStyle: 'italic' }}>
            No live classification yet — waiting for indicators / market open.
          </div>
        )}
      </section>

      {/* TODAY'S PERSISTED */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, color: '#bdc1c6', marginBottom: 10 }}>
          Today's Snapshots ({today.snapshots.length})
        </h2>
        {loading && today.snapshots.length === 0 ? (
          <div style={{ color: '#9aa0a6' }}>Loading…</div>
        ) : today.snapshots.length === 0 ? (
          <div style={{ color: '#9aa0a6', fontStyle: 'italic' }}>
            No checkpoints persisted yet today.
          </div>
        ) : (
          today.snapshots.map((s) => <SnapshotCard key={s.id} snap={s} />)
        )}
      </section>

      {/* HISTORY SUMMARY */}
      <section style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 16, color: '#bdc1c6', marginBottom: 10 }}>History (last 30 days)</h2>
        {stats && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
            <div style={{ background: '#1f2024', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}>
              Total: <strong>{stats.total}</strong>
            </div>
            <div style={{ background: '#1f2024', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}>
              Changed during day: <strong>{stats.changed}</strong>
            </div>
            <div style={{ background: '#1f2024', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}>
              Event days: <strong>{stats.events}</strong>
            </div>
            {Object.entries(stats.types).map(([t, n]) => (
              <div key={t} style={{ background: '#1f2024', padding: '8px 14px', borderRadius: 6, fontSize: 13 }}>
                <DayTypeBadge type={t} /> <strong style={{ marginLeft: 4 }}>{n}</strong>
              </div>
            ))}
          </div>
        )}
        <div style={{ overflowX: 'auto', background: '#1f2024', border: '1px solid #2e3036', borderRadius: 8 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#2a2c31', color: '#9aa0a6' }}>
                <th style={{ textAlign: 'left', padding: 10 }}>Date</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Dominant</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Final</th>
                <th style={{ textAlign: 'right', padding: 10 }}>Snapshots</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Changed</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Event</th>
                <th style={{ textAlign: 'left', padding: 10 }}>Reason</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: 14, color: '#9aa0a6', fontStyle: 'italic', textAlign: 'center' }}>
                    No history yet.
                  </td>
                </tr>
              )}
              {history.slice().reverse().map((d) => (
                <tr
                  key={d.trade_date}
                  style={{ borderTop: '1px solid #2e3036', cursor: 'pointer' }}
                  onClick={() => { setSelectedDate(d.trade_date); loadDate(d.trade_date); }}
                >
                  <td style={{ padding: 10 }}>{d.trade_date}</td>
                  <td style={{ padding: 10 }}><DayTypeBadge type={d.dominant_day_type} /></td>
                  <td style={{ padding: 10 }}>
                    <DayTypeBadge type={d.final_day_type} confidence={d.final_confidence} />
                  </td>
                  <td style={{ padding: 10, textAlign: 'right' }}>{d.snapshots}</td>
                  <td style={{ padding: 10 }}>{d.changed_during_day ? '⚠ yes' : '—'}</td>
                  <td style={{ padding: 10 }}>{d.is_event_day ? '★' : '—'}</td>
                  <td style={{ padding: 10, color: '#bdc1c6' }}>{d.final_reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* DATE DRILL-DOWN */}
      <section>
        <h2 style={{ fontSize: 16, color: '#bdc1c6', marginBottom: 10 }}>Inspect a Date</h2>
        <input
          type="date"
          value={selectedDate}
          onChange={handleDateChange}
          style={{
            background: '#1f2024',
            color: '#e8eaed',
            border: '1px solid #2e3036',
            borderRadius: 4,
            padding: '6px 10px',
            marginBottom: 14,
          }}
        />
        {datedSnapshots && (
          datedSnapshots.length === 0 ? (
            <div style={{ color: '#9aa0a6', fontStyle: 'italic' }}>
              No snapshots for {selectedDate}.
            </div>
          ) : (
            datedSnapshots.map((s) => <SnapshotCard key={s.id} snap={s} />)
          )
        )}
      </section>
    </div>
  );
}
